'use client';

import { IOrder } from '@/api/types/order';
import Image from '@/components/Image';
import { hasBlindBoxGiftFlag } from '@/domain/order/blind-box-gift';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { useTranslations } from '@/i18n/use-translations';
interface OrderItemProps {
  order: IOrder;
  isSelected: boolean;
  onSelect: (order: IOrder) => void;
  isMobile?: boolean;
}

export default function OrderItem({
  order,
  isSelected,
  onSelect,
  isMobile = false,
}: OrderItemProps) {
  const t = useTranslations('contact-zeelool.OrderItem');
  const globalT = useTranslations('contact-zeelool');
  const handleClick = () => {
    onSelect(order);
  };

  if (isMobile) {
    return (
      <div
        className={`p-12  pb-16 rounded-16 bg-white mb-8 cursor-pointer
          ${
            isSelected
              ? 'border border-primary-purple shadow-[0_2px_4px_0_rgba(70,58,232,0.2)]'
              : ''
          }
        `}
        onClick={handleClick}
      >
        {/* 订单头部 */}
        <div className="flex items-center mb-[13px]">
          <p className="text-14 font-bb font-normal text-primary-text leading-14 m-0">
            {t('Order')}: {order.order_no}
          </p>
          {(order.order_type === 4 || order.order_type === 12) && (
            <span className="block ml-14 leading-20 px-8 text-primary-purple border border-primary-purple font-bb font-normal rounded-6">
              {t('Replacement')}
            </span>
          )}
          <p className="text-14 font-bb text-auxiliary-text leading-14 ml-auto m-0">
            {globalT(`OrderSelector.orderType.${order.status}` as any)}
          </p>
        </div>

        {/* 订单商品列表 */}
        {order.order_items?.map((item) => (
          <div key={item.id} className="flex mb-10">
            <figure className="w-[70px] h-[70px] bg-white rounded-3 border border-[#dddddd] leading-[70px] m-0 flex items-center overflow-hidden mr-12">
              <Image
                src={item.image_url}
                alt={item.short_name}
                className="inline-block w-[70px] h-auto"
                width={70}
                height={70}
              />
            </figure>

            <div className="flex-1 text-12 font-normal text-auxiliary-text leading-12 h-[70px] flex flex-col justify-between">
              <p className="m-0">{item.short_name}</p>
              {!hasBlindBoxGiftFlag(item) && (
                <>
                  <p className="m-0">{item.shop_sku}</p>
                  <p className="m-0">
                    {t('Color')}: {item.frame_color}
                  </p>
                </>
              )}

              <p className="m-0">
                {t('Qty')}: {item.product_quantity}
              </p>
            </div>

            <div className="h-[70px] flex flex-col w-auto ml-12 text-12 font-bb font-normal text-primary-text leading-14 items-end justify-end">
              <p className="m-0 notranslate">
                {formatPriceNumberWithSymbol(item.item_total_original_price, order.currency)}
              </p>
            </div>
          </div>
        ))}

        {/* 订单底部总价 */}
        <div className="mt-20">
          <p className="text-right text-16 font-bb font-normal text-primary-text leading-16 pb-20 m-0">
            <label className="text-14 font-normal text-auxiliary-text leading-16">
              {t('grandTotal')}:
            </label>{' '}
            <span className="notranslate">
              {formatPriceNumberWithSymbol(order.payable, order.currency)}
            </span>
          </p>
        </div>
      </div>
    );
  }

  // PC版本 - 使用Tailwind CSS
  return (
    <div
      className={`
        overflow-hidden mt-20 w-full border rounded-lg cursor-pointer
        ${
          isSelected
            ? 'border-primary-purple shadow-[0_2px_4px_0_rgba(70,58,232,0.2)]'
            : 'border-[#e2e2e2] hover:border-primary-purple'
        }
      `}
      onClick={handleClick}
    >
      {/* 订单头部 */}
      <div className="w-full h-[46px] leading-[46px] pl-20 text-14 font-normal text-primary-text bg-[rgba(221,225,233,0.26)] border-b border-[#dce0e8]">
        <strong className="font-bb font-normal">{order.created_at}</strong>
        &emsp;{t('Order')}# {order.order_no}
        {(order.order_type === 4 || order.order_type === 12) && (
          <span className="inline-block ml-3.5 leading-5 px-2 text-primary-purple border border-primary-purple rounded-md">
            {t('Replacement')}
          </span>
        )}
      </div>

      {/* 订单商品表格 */}
      <table className="w-full">
        <tbody>
          {order.order_items?.map((item, index) => (
            <tr key={item.id}>
              <td className="px-4 py-0  border-r border-[#dce0e8]">
                <div className="flex items-center">
                  {/* 商品图片 */}
                  <div className="flex items-center justify-center w-[100px] h-[100px]">
                    <Image
                      src={item.image_url}
                      alt={item.short_name}
                      className="block w-[100px] h-auto"
                      width={100}
                      height={100}
                    />
                  </div>

                  {/* 商品信息 */}
                  <div className="ml-24 flex-1 min-w-0 leading-12 text-14 font-normal text-primary-text">
                    <strong className="font-bb font-normal">{item.short_name}</strong>{' '}
                    {!hasBlindBoxGiftFlag(item) && (
                      <span className="text-[#a9a9a9]">({item.shop_sku})</span>
                    )}
                    {!hasBlindBoxGiftFlag(item) && (
                      <>
                        <br />
                        <br />
                        {item.frame_color}
                        <br />
                        <br />
                        {item.prescription?.prescription_type_name ??
                          item.prescription?.prescription_type}
                      </>
                    )}
                  </div>

                  {/* 数量 */}
                  <div className="mr-4 w-[72px] leading-3 text-center text-16 font-normal text-primary-text">
                    X{item.product_quantity}
                  </div>

                  {/* 单价 */}
                  <div className="w-[128px] leading-3 text-center text-16 font-bb font-normal text-primary-text notranslate">
                    {formatPriceNumberWithSymbol(item.item_total_original_price, order.currency)}
                  </div>
                </div>
              </td>

              {/* 总价和状态 - 只在第一行显示 */}
              {index === 0 && (
                <>
                  <td
                    className="text-14 font-normal text-primary-text leading-[14px] w-[124px] px-16 text-center border-r border-[#e2e2e2]"
                    rowSpan={order.order_items?.length}
                  >
                    {t('grandTotal')}:
                    <br />
                    <strong className="block pt-4 font-bb font-normal notranslate">
                      {formatPriceNumberWithSymbol(order.payable, order.currency)}
                    </strong>
                  </td>
                  <td
                    className="text-14 font-normal text-[#bc1b1b] leading-[14px] text-center px-8"
                    rowSpan={order.order_items?.length}
                  >
                    {globalT(`OrderSelector.orderType.${order.status}` as any)}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
